const PERSISTABLE_KEYS = new Set([
  'organizations',
  'projects',
  'tasks',
  'contacts',
  'members',
  'entities',
  'invoices',
  'products',
  // The `time-entries` root is deliberately absent: those lists/totals change
  // across browser, desktop, and realtime flows, and stale hours on an
  // editable timesheet are worse than a loading state. The Today dashboard's
  // read-only time summaries are the documented exception — see
  // TODAY_DASHBOARD_KEYS below.
  'contracts',
  'client-portal',
  // Table modules — restored instantly so lists render from cache on revisit
  'contacts',
  'deals',
  'notes',
  'documents',
  'forms',
  'suppliers',
  'tickets',
  'events',
  'custom-collections',
  'sprints',
  'cycles',
  // Column metadata — these gate table column building; persisting them means
  // tables skip the skeleton and render with the correct columns immediately
  'custom-fields',
  'column-preferences',
  'crm-stages',
  'ticket-types',
])

/**
 * Today-dashboard query roots.
 *
 * Today is the app's landing surface, and every one of its widgets gates its
 * own skeleton on its own query — so a cold open is a wall of shimmer for
 * several seconds. These roots are persisted like the rest, but they also get
 * a much longer *restore* window than everything else (see
 * TODAY_DASHBOARD_MAX_AGE in QueryCacheProvider): the dashboard paints from
 * the last-known snapshot immediately and revalidates silently behind it.
 *
 * Safe to show slightly stale because every one of these is a read-only
 * summary that is refetched on mount, and the risky-to-stale keys carry the
 * date in the key itself (`today-time-stats` → yesterday's snapshot simply
 * never matches today's key).
 *
 * This includes `team-pulse`, whose time summary reports other people's hours.
 * That is the one place a time-derived total is persisted, and it is a
 * deliberate product decision: the widget is a read-only glance, its time
 * query is date-keyed (`['team-pulse', 'time', dateStr]`), and it refetches on
 * mount. The blanket "never persist time entries" rule above still holds for
 * every editable timesheet surface.
 */
const TODAY_DASHBOARD_KEYS = new Set([
  // Schedule / agenda widget (today + tomorrow calendar range)
  'today-schedule',
  // Timer widget's "logged today" stats
  'today-time-stats',
  // Action rail (overdue tasks, approvals, unread notifications)
  'action-center',
  // Unified inbox widget (notifications + notes + approvals)
  'unified-inbox',
  // My Work / My Tasks widget — the tallest widget on the page
  'my-tasks',
  'my-work',
  // Team Pulse widget (members, pay periods, and the date-keyed team time
  // summary) — see the note above about persisting time-derived totals
  'team-pulse',
  // Today P&L / revenue overview widget (`['today', 'revenue-overview', …]`)
  'today',
])

/** True when the key belongs to a Today-dashboard widget (long restore window). */
export function isTodayDashboardQuery(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0]
  if (typeof root !== 'string') return false
  return TODAY_DASHBOARD_KEYS.has(root)
}

export function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0]
  if (typeof root !== 'string') return false
  return PERSISTABLE_KEYS.has(root) || TODAY_DASHBOARD_KEYS.has(root)
}
